import { IOrderClause } from '@tableau/data-catalog-client-components';
import { DataGrid } from '@tableau/widgets-datagrid';
import React from 'react';
import { defaultMemoize } from 'reselect';

import { SortableHeader } from '../../../app/spaces/ListView/SortableHeader';
import { Formatters, Messages } from '../../../codegen/Localize';
import { CommonStyles } from '../../../common/react/CommonStyles';
import { getHeightForRows, SortableDataGrid } from '../../../common/react/components/dataGrid/SortableDataGrid3';
import { imgPath, imgUrl } from '../../../common/utilities/ImgUrl';
import { cssClass, flexColumnStyle } from '../../../style';
import { FeatureFlags } from '../../../ts/FeatureFlags';
import {
  IPublishedConnectionDetailInfo,
  IPublishedConnectionTableInfo
} from '../../../ts/models/IPublishedConnectionInfo';
import { ISliceable } from '../../../ts/models/ISliceable';
import { SliceableHelper } from '../../../ts/helpers/SliceableHelper';
import { TablesGridDataKey } from './PublishedConnectionTablesGridTypes';
import { PlainTextCell, PlainTextCellWithIcon } from '../../../app/spaces/ListView/dataGridCells/PlainTextAndNumberCells';
import { ContentTypeCell } from '../../../app/spaces/ListView/dataGridCells/ContentTypeCell';
import { ExtractStatus } from './extractStatus/ExtractStatus';
import { TabStyles } from '@tableau/widgets';
import { ExtractStatusInfo } from './extractStatus/ExtractStatusInfo';

const cssPrefix = 'PublishedConnectionTablesGrid';

const rootClass = cssClass(cssPrefix, 'root', flexColumnStyle, {
  position: 'relative',
  minHeight: getHeightForRows(2),
  paddingBottom: CommonStyles.Sizes.LargeSpace,
  borderBottom: '1px solid black',
  width: '100%'
});

const completedIconClass = cssClass(cssPrefix, 'status_active_16px_medium', {
  backgroundImage: imgUrl('status_active_16px_medium.svg'),
  display: 'inline-block',
  height: '16px',
  marginRight: TabStyles.Spacing.SmallSpace,
  width: '16px',
  verticalAlign: 'text-bottom'
});

function getExtractStatusIconClass(extractStatusInfo: ExtractStatusInfo): string {
  if (extractStatusInfo.status === ExtractStatus.Completed) {
    return completedIconClass;
  }
  return '';
}

function getExtractStatusText(extractStatusInfo: ExtractStatusInfo): string {
  const { status, completedDate } = extractStatusInfo;
  if (status === ExtractStatus.Completed) {
    return Messages.extracted({FORMATTED_DATE_TIME: Formatters.formatMediumDateTimeWithoutSeconds(completedDate)});
  }
  return Messages.publishedConnectionLive();
}

function getRowData(
  tables: IPublishedConnectionTableInfo[],
  currentSort: IOrderClause
): (orderClause: IOrderClause[]) => ISliceable<any> {

  const tableRows: any[] = tables.map((table: IPublishedConnectionTableInfo) => {
    return {
      NAME: table.alias,
      POLICIES: Messages.publishedConnectionNoPolicies(), // table.policies
      EXTRACT: { status: ExtractStatus.Live} as ExtractStatusInfo, // table.extractStatusInfo,
      DATABASE: table.databaseName,
      SCHEMA: table.schemaName
    };
  });
  if (currentSort.ascending) {
    tableRows.sort((a, b) => (a[currentSort.field] > b[currentSort.field]) ? 1 : -1);
  } else {
    tableRows.sort((a, b) => (a[currentSort.field] > b[currentSort.field]) ? -1 : 1);
  }
  return (_orderClause: IOrderClause[]) => SliceableHelper.fromArray(tableRows);
}

function hasDatabase(tables: IPublishedConnectionTableInfo[]): boolean {
  return tables.length > 0 && !!tables[0].databaseName;
}

function hasSchema(tables: IPublishedConnectionTableInfo[]): boolean {
  return tables.length > 0 && !!tables[0].schemaName;
}

function getTableColumns(tables: IPublishedConnectionTableInfo[], sortableHeaderFormatter: React.Factory<any>): DataGrid.Column[] {
  const nameColumn: DataGrid.Column = {
    name: TablesGridDataKey.NAME,
    dataKey: TablesGridDataKey.NAME,
    sortable: true,
    headerText: Messages.column_name(),
    growToFit: true,
    headerFormatter: sortableHeaderFormatter,
    cellFormatter: PlainTextCell(TablesGridDataKey.NAME)
  };
  const typeColumn = {
    name: TablesGridDataKey.TYPE,
    dataKey: TablesGridDataKey.TYPE,
    resizable: false,
    sortable: false,
    headerText: '',
    headerFormatter: sortableHeaderFormatter,
    cellFormatter: ContentTypeCell({
      imageUrlForRow: () => imgPath('published_connection_table_medium.svg'),
      titleForIcon: () => Messages.table(),
      isCertified: () => false,
      getTooltipProps: () => null
    })
  };
  const extractColumn = {
    name: TablesGridDataKey.EXTRACT,
    dataKey: TablesGridDataKey.EXTRACT,
    headerText: Messages.status(),
    resizable: true,
    sortable: true,
    headerFormatter: sortableHeaderFormatter,
    cellFormatter: PlainTextCellWithIcon((row: any) => {
      const rowData = row[TablesGridDataKey.EXTRACT];
      return {
        text: getExtractStatusText(rowData),
        iconClass: getExtractStatusIconClass(rowData)
      };
    })
  };
  const policiesColumn = {
    name: TablesGridDataKey.POLICIES,
    dataKey: TablesGridDataKey.POLICIES,
    headerText: Messages.policies(),
    resizable: true,
    sortable: true,
    headerFormatter: sortableHeaderFormatter,
    cellFormatter: PlainTextCell(TablesGridDataKey.POLICIES)
  };
  const databaseColumn = {
    name: TablesGridDataKey.DATABASE,
    dataKey: TablesGridDataKey.DATABASE,
    headerText: Messages.databaseName(),
    resizable: true,
    sortable: true,
    headerFormatter: sortableHeaderFormatter,
    cellFormatter: PlainTextCell(TablesGridDataKey.DATABASE)
  };

  const schemaColumn = {
    name: TablesGridDataKey.SCHEMA,
    dataKey: TablesGridDataKey.SCHEMA,
    headerText: Messages.schemaName(),
    resizable: true,
    sortable: true,
    headerFormatter: sortableHeaderFormatter,
    cellFormatter: PlainTextCell(TablesGridDataKey.SCHEMA)
  };

  const columns: DataGrid.Column[] = [
    typeColumn,
    nameColumn
  ];
  if (hasDatabase(tables)) {
    columns.push(databaseColumn);
  }
  if (hasSchema(tables)) {
    columns.push(schemaColumn);
  }
  if (FeatureFlags.publishedConnectionTablesExtractColumnEnabled) {
    columns.push(extractColumn);
  }
  if (FeatureFlags.publishedConnectionTablesPoliciesColumnEnabled) {
    columns.push(policiesColumn);
  }
  return columns;
}

export type PublishedConnectionTablesGridState = Readonly<{
  currentSort: IOrderClause;
}>;

export type PublishedConnectionTablesGridProps = Readonly<{
  publishedConnectionDetailInfo: IPublishedConnectionDetailInfo;
}>;

export class PublishedConnectionTablesGrid extends React.PureComponent<PublishedConnectionTablesGridProps, PublishedConnectionTablesGridState> {
  private memoizedGetRowData = defaultMemoize(getRowData);
  private memoizedTableColumns = defaultMemoize(getTableColumns);

  constructor(props: PublishedConnectionTablesGridProps) {
    super(props);
    this.state = {
      currentSort:
        {
          field: TablesGridDataKey.NAME,
          ascending: true
        },
    };
  }

  private emptyChild(): JSX.Element {
    return (
      <div className={'tb-data-grid-empty'}>
        <div className={'tb-data-grid-empty-title'}>{Messages.publishedConnectionTablesEmpty()}</div>
      </div>
    );
  }

  private changeSort = (field: string) => {
    if (field === this.state.currentSort.field) {
      this.setState({
        currentSort:
          {
            field: field,
            ascending: !this.state.currentSort.ascending
          }
      });
    } else {
      this.setState({
        currentSort:
          {
            field: field,
            ascending: true
          }
      });
    }
  };

  public render(): JSX.Element {
    const numOfRows: number = 0;
    const sortableHeaderFormatter = SortableHeader.Formatter([this.state.currentSort], this.changeSort);
    const sliceable: (orderClause: IOrderClause[]) => ISliceable<any> = this.memoizedGetRowData(
      this.props.publishedConnectionDetailInfo.tables,
      this.state.currentSort
    );
    const heightStyle: React.CSSProperties = {
      height: numOfRows ? getHeightForRows(numOfRows + 1) : undefined
    };
    const columns = this.memoizedTableColumns(this.props.publishedConnectionDetailInfo.tables, sortableHeaderFormatter);

    return (
      <div className={rootClass} style={heightStyle}>
        <SortableDataGrid
          columns={columns}
          getSliceable={sliceable}
          emptyChild={this.emptyChild()}
        />
      </div>
    );
  }
}
